import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useAssetSettings } from '../context/AssetSettingsContext';
import { getAssetSettings, updateAssetSettings } from '../services/assetService';
import { ALL_ASSET_TYPES, AssetType, assetTypeLabelKey } from '../types/Asset';
import catalog from '../../catalog/Catalog.module.css';
import { AssetTypeIcon, ButtonWithIcon, SaveIcon } from './AssetIcons';
import iconStyles from './AssetIcons.module.css';

export const AssetSettingsPage: React.FC = () => {
  const { t } = useTranslation(['assets', 'common']);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { refresh } = useAssetSettings();
  const [selectedTypes, setSelectedTypes] = React.useState<AssetType[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const canUpdate = hasPermission(PermissionCodes.assetsUpdate);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await getAssetSettings();
        setSelectedTypes(data.enabledTypes);
      } catch (error) {
        setGeneralError(error instanceof Error ? error.message : t('failedToLoadSettings', { ns: 'assets' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [t]);

  const toggleType = (type: AssetType) => {
    setSelectedTypes(previous => (
      previous.includes(type)
        ? previous.filter(item => item !== type)
        : [...previous, type]
    ));
  };

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setGeneralError(null);
    try {
      const updated = await updateAssetSettings({ enabledTypes: selectedTypes });
      setSelectedTypes(updated.enabledTypes);
      await refresh();
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSaveSettings', { ns: 'assets' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loadingSettings', { ns: 'assets' })} /></div>;
  }

  return (
    <div className={catalog.page}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'assets' }), path: '/assets' },
        { label: t('settingsTitle', { ns: 'assets' }), path: '/assets/settings' },
      ]} />
      <h2 className={catalog.title}>{t('settingsTitle', { ns: 'assets' })}</h2>
      <p className={catalog.intro}>{t('settingsIntro', { ns: 'assets' })}</p>
      {generalError ? <div className="error">{generalError}</div> : null}
      <form className={catalog.form} onSubmit={onSave}>
        <div className={catalog.field}>
          <label className={catalog.label}>{t('enabledTypes', { ns: 'assets' })}</label>
          <div className={iconStyles.typeOptions}>
            {ALL_ASSET_TYPES.map(type => (
              <label key={type} className={iconStyles.typeOption}>
                <input
                  type="checkbox"
                  checked={selectedTypes.includes(type)}
                  disabled={!canUpdate}
                  onChange={() => toggleType(type)}
                />
                <AssetTypeIcon type={type} className={iconStyles.typeOptionIcon} />
                {t(assetTypeLabelKey(type), { ns: 'assets', defaultValue: type })}
              </label>
            ))}
          </div>
          <p className={catalog.fieldHint}>{t('enabledTypesHint', { ns: 'assets' })}</p>
        </div>
        <div className={catalog.actions}>
          <button className="btn btn-secondary" type="button" onClick={() => navigate('/assets')}>{t('back', { ns: 'assets' })}</button>
          {canUpdate ? (
            <button className={`btn btn-primary ${iconStyles.buttonWithIcon}`} type="submit" disabled={saving}>
              <ButtonWithIcon icon={<SaveIcon />}>{t('save', { ns: 'assets' })}</ButtonWithIcon>
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default AssetSettingsPage;
